import { activeScrollTarget } from '@/components/ui/activeScrollTarget';
import { PAGE_GUTTER, usePageFrame } from '@/components/ui/pageFrame';
import { useIsFocused } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useEffect, useRef } from 'react';
import { FlatList, Platform, type FlatListProps } from 'react-native';

/**
 * PageFlatList — [[PageScrollView]]'s frame around a virtualized page.
 *
 * Same gutter, same under-header start, same bottom gap, same pull-to-refresh
 * (that file's doc block is the why for each of them); the only difference is
 * WHAT scrolls. Reach for it when the page's body is a long list, so it can page
 * itself with `onEndReached` and mount only the rows on screen.
 *
 * Everything above and below that body goes in `ListHeaderComponent` /
 * `ListFooterComponent` — the list has to BE the page's scroller, not sit inside
 * one. A VirtualizedList nested in a same-orientation ScrollView loses its
 * windowing and never reaches its own end (RN warns about exactly this), so
 * "scroll view with a list in it" is not an option the frame can offer.
 *
 * The frame props sit AFTER the spread on purpose: like PageScrollView's, they're
 * not overridable per screen. `contentContainerStyle` in particular is load
 * bearing — it carries the gutter a `numColumns` row measures itself against.
 *
 * While focused, on web, it also publishes its DOM node to
 * [[activeScrollTarget]] — see [[PageScrollView]]'s doc block for why.
 */
function PageFlatList<T>({
  headerTransparent = true,
  onRefresh,
  isRefreshing = false,
  ...rest
}: FlatListProps<T> & {
  headerTransparent?: boolean;
  /** Omit for a page that doesn't pull to refresh — then no control is built. */
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  const { paddingTop, paddingBottom, refreshControl } = usePageFrame({
    headerTransparent,
    onRefresh,
    isRefreshing,
  });

  const listRef = useRef<FlatList<T>>(null);
  const isFocused = useIsFocused();
  useEffect(() => {
    if (Platform.OS !== 'web' || !isFocused) return;
    const node = listRef.current?.getScrollableNode();
    if (!node) return;
    activeScrollTarget.current = node;
    return () => {
      if (activeScrollTarget.current === node)
        activeScrollTarget.current = null;
    };
  }, [isFocused]);

  return (
    <FlatList
      ref={listRef}
      {...rest}
      style={{ flex: 1 }}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      refreshControl={refreshControl}
      contentContainerStyle={{
        paddingHorizontal: PAGE_GUTTER,
        paddingTop,
        paddingBottom,
      }}
    />
  );
}

export default PageFlatList;
